import { normalizeFolderPath } from './folders';
import { setDocFolder, setDocRank } from './library';
import { planMove } from './rank';
import type { DocMeta } from './db';

/**
 * Moving documents within the manual order, as the library shows it.
 *
 * A document is ordered among its siblings: same folder, and same side of the
 * pinned divide, since pinned documents always list first whatever the order.
 * `docs` must be the list in manual order, which is what the sidebar holds.
 */

const folderOf = (d: Pick<DocMeta, 'folder'>) => normalizeFolderPath(d.folder);

function siblings(docs: DocMeta[], of: DocMeta): DocMeta[] {
  return docs.filter((d) => folderOf(d) === folderOf(of) && !!d.pinned === !!of.pinned);
}

async function applyPlan(group: DocMeta[], movedId: string, toIndex: number): Promise<void> {
  for (const update of planMove(group, movedId, toIndex)) {
    await setDocRank(update.id, update.rank ?? null);
  }
}

/**
 * Drop `movedId` just before or after `targetId`. Landing next to a document in
 * another folder moves it into that folder too, which is what the drop looks
 * like it does. Across the pinned divide only the folder changes: the position
 * could not be shown.
 */
export async function placeNextTo(
  docs: DocMeta[],
  movedId: string,
  targetId: string,
  after: boolean,
): Promise<void> {
  const moved = docs.find((d) => d.id === movedId);
  const target = docs.find((d) => d.id === targetId);
  if (!moved || !target || moved.id === target.id) return;

  if (folderOf(moved) !== folderOf(target)) await setDocFolder(movedId, folderOf(target));
  if (!!moved.pinned !== !!target.pinned) return;

  const group = siblings(docs, target);
  const others = group.filter((d) => d.id !== movedId);
  const index = others.findIndex((d) => d.id === targetId) + (after ? 1 : 0);
  await applyPlan(group, movedId, index);
}

/** Move one place up (-1) or down (+1) among siblings. False at either end. */
export async function moveByStep(docs: DocMeta[], id: string, delta: -1 | 1): Promise<boolean> {
  const moved = docs.find((d) => d.id === id);
  if (!moved) return false;
  const group = siblings(docs, moved);
  const index = group.findIndex((d) => d.id === id);
  const next = index + delta;
  if (next < 0 || next >= group.length) return false;
  await applyPlan(group, id, next);
  return true;
}
